import { ALL_DATA_TYPES } from '@repo/advanced-filters';
import { type DropdownInputProps } from '@repo/ui';
import {
  type DataTypeConfigType, DropdownRenderTypeConfig, emailRule, mobileNumberRule, requiredRule, useDebounce,
} from '@repo/utils';
import {
  type DatePickerProps,
  Form, type FormItemProps, type SelectProps, type TimePickerProps, Typography,
} from 'antd';
import { type Rule } from 'antd/es/form';
import { type DefaultOptionType } from 'antd/es/select';
import clsx from 'clsx';
import DataTypeSpecificInput from 'crm-atoms/components/DataTypeSpecificInput/DataTypeSpecificInput';
import { RenderDataByDataType } from 'crm-atoms/components/RenderLeadDataByDataType';
import { type SupportedEntityValueType } from 'crm-atoms/data';
import { useFormFieldItemOptions } from 'crm-atoms/hooks';
import { getParsedDataTypeBySchemaName } from 'crm-atoms/utils/dataTypes';
import dayjs from 'dayjs';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import styles from './FormFieldItem.module.scss';

function FormLabelRender({ label, required, className }: { label: string, required?: boolean, className?: string }) {
  return (
    <Typography.Text className={clsx(styles.label, className)}>
      <span className={styles.required}>{required ? '*' : ''}</span>
      {`${label}:`}
    </Typography.Text>
  );
}

export interface FormFieldItemProps {
  value?: any;
  labelClassName?: string;
  displayValue?: string;
  mode: 'view' | 'edit';
  displayName: string;
  dataType: string;
  dataTypeConfig?: DataTypeConfigType;
  placeholder: string;
  schemaName: string;
  formItemProps?: FormItemProps;
  required?: boolean;
  entityId: string | number;
  entityName: SupportedEntityValueType;
  dropdownInputProps?: DropdownInputProps;
  ownerInputProps?: SelectProps;
  dateInputProps?: DatePickerProps;
  timeInputProps?: TimePickerProps;
  isOptionsRemoteSearchable?: boolean;
  disabled?: boolean;
  handleDropdownFilter?: (value: DefaultOptionType) => boolean;
}

export default function FormFieldItem({
  value,
  labelClassName,
  displayValue,
  mode,
  displayName,
  dataType,
  dataTypeConfig,
  placeholder,
  schemaName,
  formItemProps,
  required,
  entityId,
  entityName,
  dropdownInputProps,
  ownerInputProps,
  dateInputProps,
  timeInputProps,
  isOptionsRemoteSearchable = true,
  disabled = false,
  handleDropdownFilter,
}: FormFieldItemProps) {
  const [dropdownSearchString, setDropdownSearchString] = useState<string | null>(null);
  const debouncedSearchString = useDebounce(dropdownSearchString);

  const form = Form.useFormInstance();

  const parsedDataType = getParsedDataTypeBySchemaName(dataType, schemaName);

  const {
    queryTrigger: fetchOptionsTrigger,
    parsedResponse: data,
    queryResponse: {
      isFetching: optionsLoading,
    },
    document,
  } = useFormFieldItemOptions(entityName, schemaName, entityId);

  const handleFetchOptions = useCallback(() => {
    fetchOptionsTrigger({
      document,
      variables: {
        id: entityId,
        searchString: debouncedSearchString,
      },
    }, true);
  }, [document, debouncedSearchString, entityId, fetchOptionsTrigger]);

  useEffect(() => {
    if (isOptionsRemoteSearchable && typeof debouncedSearchString === 'string') {
      handleFetchOptions();
    }
  }, [debouncedSearchString, handleFetchOptions, isOptionsRemoteSearchable]);

  useEffect(() => {
    if (form) {
      const defaultOption = data?.find((attr) => attr.isDefault);
      if (defaultOption) {
        form.setFieldValue(schemaName, defaultOption.label);
      }
    }
  }, [data, form, schemaName]);

  useEffect(() => {
    if (form) {
      if (parsedDataType === 'multi_select' && !Array.isArray(value)) {
        form.setFieldValue(schemaName, [value]);
      }
      form.setFieldValue(schemaName, value);
    }
  }, [form, schemaName, value, mode, parsedDataType]);

  const formRules: Rule[] = useMemo(() => {
    const rules = [];

    rules.push(requiredRule(required ?? false, `Please input ${displayName}`));
    if (dataTypeConfig?.inputTypeConfig?.toLowerCase() === 'email') {
      rules.push(emailRule(true, 'Please enter a valid email'));
    }

    if (dataTypeConfig?.inputTypeConfig?.toLowerCase() === 'phone') {
      rules.push(mobileNumberRule(true, 'Please enter a valid phone number'));
    }

    return rules;
  }, [dataTypeConfig?.inputTypeConfig, displayName, required]);

  const modifiedOptions = useMemo(() => {
    const options = data?.map((attr) => ({
      label: attr.label,
      value: attr.label,
    })) || [];

    return handleDropdownFilter ? options.filter(handleDropdownFilter) : options;
  }, [data, handleDropdownFilter]);

  if (mode === 'view' && !displayValue) {
    return null;
  } else if (mode === 'edit') {
    return (
      <Form.Item
        label={<FormLabelRender label={displayName} required={required} className={labelClassName} />}
        name={schemaName}
        rules={formRules}
        colon={false}
        required={false}
        {...formItemProps}
      >
        <DataTypeSpecificInput
          value={value}
          dataType={parsedDataType ?? ALL_DATA_TYPES.string}
          dataTypeConfig={dataTypeConfig}
          inputProps={{ placeholder: `Add ${placeholder}` }}
          inputNumberProps={{ placeholder: `Add ${placeholder}` }}
          dropdownInputProps={{
            remoteSearchable: Boolean(dataTypeConfig?.dropdownRenderType
              && dataTypeConfig.dropdownRenderType === DropdownRenderTypeConfig.REMOTE_SEARCHABLE),
            searchString: dropdownSearchString ?? '',
            setSearchString: setDropdownSearchString,
            placeholder: `Select ${placeholder}`,
            options: modifiedOptions,
            optionsLoading,
            prefetchOptions: false,
            handleFetchOptions,
            showRadioGroup: (dataTypeConfig?.dropdownRenderType === DropdownRenderTypeConfig.RADIO_GROUP),
            ...dropdownInputProps,
          }}
          leadStageSourceProps={{
            ...dropdownInputProps,
          }}
          referenceInputProps={{
            changeValue: 'label',
          }}
          ownerInputProps={ownerInputProps}
          dateInputProps={{
            ...(dataTypeConfig?.pastTimeNotAllowed ? {
              minDate: dayjs(),
            } : {}),
            ...dateInputProps,
          }}
          timeInputProps={timeInputProps}
          disabled={disabled}
        />
      </Form.Item>
    );
  } else {
    return (
      <Form.Item
        label={<FormLabelRender label={displayName} />}
        colon={false}
        rules={[]}
        {...formItemProps}
      >
        <Typography.Text>
          <RenderDataByDataType
            value={displayValue}
            dataType={parsedDataType as any}
            className={styles.valueContainer}
          />
        </Typography.Text>
      </Form.Item>
    );
  }
}
